/**
 * This file contains custom utility functions that serve as lightweight replacements for lodash functions.
 * The purpose is to eliminate the heavy lodash dependency while maintaining similar functionality used across the application.
 */

/**
 * Checks if value is classified as a String primitive or object.
 * @param value The value to check.
 * @returns Returns true if value is a string, else false.
 */
export function isString(value: any): value is string {
  return typeof value === 'string' || value instanceof String;
}

/**
 * Checks if value is classified as a Number primitive or object.
 * @param value The value to check.
 * @returns Returns true if value is a number, else false.
 */
export function isNumber(value: any): value is number {
  return typeof value === 'number' && !isNaN(value);
}

/**
 * Checks if value is null or undefined.
 * @param value The value to check.
 * @returns Returns true if value is null or undefined, else false.
 */
export function isNil(value: any): value is null | undefined {
  return value === null || value === undefined;
}

/**
 * Checks if value is classified as a Date object.
 * @param value The value to check.
 * @returns Returns true if value is a date, else false.
 */
export function isDate(value: any): value is Date {
  return value instanceof Date && !isNaN(value.getTime());
}

/**
 * Gets the value at path of object. If the resolved value is undefined, the defaultValue is returned in its place.
 * @param object The object to query.
 * @param path The path of the property to get.
 * @param defaultValue The value returned for undefined resolved values.
 * @returns Returns the resolved value.
 */
export function get(
  object: any,
  path: string | string[],
  defaultValue?: any
): any {
  if (object == null) {
    return defaultValue;
  }

  const keys = Array.isArray(path)
    ? path
    : path.replace(/\[(\d+)\]/g, '.$1').split('.');
  let result = object;

  for (const key of keys) {
    if (result == null || !Object.prototype.hasOwnProperty.call(result, key)) {
      return defaultValue;
    }
    result = result[key];
  }

  return result === undefined ? defaultValue : result;
}

/**
 * Checks if value is in collection. If collection is a string, it's checked for a substring of value,
 * otherwise SameValueZero is used for equality comparisons. If fromIndex is negative, it's used as the offset from the end of collection.
 * @param collection The collection to inspect.
 * @param value The value to search for.
 * @param fromIndex The index to search from.
 * @returns Returns true if value is found, else false.
 */
export function includes(
  collection: any,
  value: any,
  fromIndex?: number
): boolean {
  if (isString(collection)) {
    return collection.includes(value, fromIndex);
  }
  if (Array.isArray(collection)) {
    return collection.includes(value, fromIndex);
  }
  if (typeof collection === 'object' && collection !== null) {
    return Object.values(collection).includes(value);
  }
  return false;
}

/**
 * Performs a deep comparison between two values to determine if they are equivalent.
 * @param value The value to compare.
 * @param other The other value to compare.
 * @returns Returns true if the values are equivalent, else false.
 */
export function isEqual(value: any, other: any): boolean {
  if (value === other) {
    return true;
  }

  if (
    value === null ||
    value === undefined ||
    other === null ||
    other === undefined
  ) {
    return value === other;
  }

  if (value instanceof Date && other instanceof Date) {
    return value.getTime() === other.getTime();
  }

  if (value instanceof RegExp && other instanceof RegExp) {
    return value.source === other.source && value.flags === other.flags;
  }

  if (Array.isArray(value) && Array.isArray(other)) {
    if (value.length !== other.length) {
      return false;
    }
    for (let i = 0; i < value.length; i++) {
      if (!isEqual(value[i], other[i])) {
        return false;
      }
    }
    return true;
  }

  if (typeof value === 'object' && typeof other === 'object') {
    if (Array.isArray(value) || Array.isArray(other)) {
      return false; // One is array other is object
    }

    const keysA = Object.keys(value);
    const keysB = Object.keys(other);

    if (keysA.length !== keysB.length) {
      return false;
    }

    for (const key of keysA) {
      if (!Object.prototype.hasOwnProperty.call(other, key)) {
        return false;
      }
      if (!isEqual(value[key], other[key])) {
        return false;
      }
    }
    return true;
  }

  return false;
}

/**
 * This method is like _.clone except that it recursively clones value.
 * @param value The value to recursively clone.
 * @returns Returns the deep cloned value.
 */
export function cloneDeep<T>(value: T): T {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (value instanceof Date) {
    return new Date(value.getTime()) as any;
  }

  if (value instanceof RegExp) {
    return new RegExp(value.source, value.flags) as any;
  }

  if (Array.isArray(value)) {
    return value.map((item) => cloneDeep(item)) as any;
  }

  const clonedObj = {} as T;
  for (const key in value) {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      clonedObj[key] = cloneDeep(value[key]);
    }
  }
  return clonedObj;
}

/**
 * Iterates over elements of collection, returning an array of all elements predicate returns truthy for.
 * @param collection The collection to iterate over.
 * @param predicate The function invoked per iteration.
 * @returns Returns the new filtered array.
 */
export function filter<T>(collection: T[], predicate: any): T[] {
  if (!Array.isArray(collection)) {
    return [];
  }

  if (typeof predicate === 'function') {
    return collection.filter(predicate);
  }

  if (typeof predicate === 'object' && predicate !== null) {
    return collection.filter((item) => {
      for (const key in predicate) {
        if (get(item, key) !== predicate[key]) {
          return false;
        }
      }
      return true;
    });
  }

  // Default fallback if predicate is something else (not fully compliant with all lodash cases but sufficient for identified usage)
  return collection.filter((item) => (item as any) === predicate);
}

/**
 * Creates an array of elements, sorted in ascending order by the results of running each element through each iteratees.
 * @param collection The collection to iterate over.
 * @param iteratees The iteratees to sort by.
 * @returns Returns the new sorted array.
 */
export function sortBy<T>(collection: T[], iteratees?: any): T[] {
  if (!Array.isArray(collection)) {
    return [];
  }

  const data = [...collection];

  if (!iteratees) {
    return data.sort();
  }

  return data.sort((a: any, b: any) => {
    let valA = a;
    let valB = b;

    if (typeof iteratees === 'string') {
      valA = get(a, iteratees);
      valB = get(b, iteratees);
    } else if (typeof iteratees === 'function') {
      valA = iteratees(a);
      valB = iteratees(b);
    } else if (Array.isArray(iteratees)) {
      // Simple support for array of strings for now as typically used
      for (const iter of iteratees) {
        const vA = get(a, iter);
        const vB = get(b, iter);
        if (vA < vB) {
          return -1;
        }
        if (vA > vB) {
          return 1;
        }
      }
      return 0;
    }

    if (valA < valB) {
      return -1;
    }
    if (valA > valB) {
      return 1;
    }
    return 0;
  });
}

/**
 * Checks if value is a plain object, that is, an object created by the Object constructor or one with a [[Prototype]] of null.
 * @param value The value to check.
 * @returns Returns true if value is a plain object, else false.
 */
export function isPlainObject(value: any): boolean {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === null || proto === Object.prototype;
}

/**
 * Creates a shallow clone of value.
 * @param value The value to clone.
 * @returns Returns the cloned value.
 */
export function clone<T>(value: T): T {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return [...value] as any;
  }
  return { ...value };
}

/**
 * Converts string, as space separated words, to lower case.
 * @param string The string to convert.
 * @returns Returns the lower cased string.
 */
export function lowerCase(string: string): string {
  if (!string) {
    return '';
  }
  return string
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[\s_-]+/g, ' ')
    .toLowerCase();
}

/**
 * Checks if path is a direct property of object.
 * @param object The object to query.
 * @param path The path to check.
 * @returns Returns true if path exists, else false.
 */
export function has(object: any, path: string | string[]): boolean {
  if (object == null) {
    return false;
  }
  const keys = Array.isArray(path)
    ? path
    : path.replace(/\[(\d+)\]/g, '.$1').split('.');
  let result = object;
  for (const key of keys) {
    if (result == null || !Object.prototype.hasOwnProperty.call(result, key)) {
      return false;
    }
    result = result[key];
  }
  return true;
}

/**
 * Creates an object composed of the picked object properties.
 * @param object The source object.
 * @param paths The property paths to pick.
 * @returns Returns the new object.
 */
export function pick(object: any, paths: string | string[]): any {
  if (object == null) {
    return {};
  }
  const result: any = {};
  const props = Array.isArray(paths) ? paths : [paths];
  for (const prop of props) {
    const value = get(object, prop);
    if (value !== undefined) {
      // This is a simplified pick that sets the value at the top level of the new object
      // Lodash pick typically maintains structure or picks specific keys.
      // If correct structure is needed, we need 'set' function, but for simple key picking:
      result[prop] = value;
    }
  }
  return result;
}

/**
 * Creates an object composed of the object properties predicate returns truthy for.
 * @param object The source object.
 * @param predicate The function invoked per iteration.
 * @returns Returns the new object.
 */
export function pickBy(
  object: any,
  predicate: (value: any, key: string) => boolean
): any {
  if (object == null) {
    return {};
  }
  const result: any = {};
  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      if (predicate(object[key], key)) {
        result[key] = object[key];
      }
    }
  }
  return result;
}

/**
 * Checks if value is an empty object, collection, map, or set.
 * @param value The value to check.
 * @returns Returns true if value is empty, else false.
 */
export function isEmpty(value: any): boolean {
  if (value == null) {
    return true;
  }
  if (Array.isArray(value) || typeof value === 'string') {
    return value.length === 0;
  }
  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }
  return true;
}

/**
 * Converts value to a string. An empty string is returned for null and undefined values.
 * @param value The value to convert.
 * @returns Returns the converted string.
 */
export function toString(value: any): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    return value
      .map((other) => (other == null ? other : toString(other)))
      .join(',');
  }

  const result = value + '';
  return result == '0' && 1 / value == -Infinity ? '-0' : result;
}

/**
 * Creates an array of array values not included in the other given arrays using SameValueZero for equality comparisons.
 * @param array The array to inspect.
 * @param values The values to exclude.
 * @returns Returns the new array of filtered values.
 */
export function difference<T>(array: T[], values: T[]): T[] {
  if (!Array.isArray(array)) {
    return [];
  }
  if (!Array.isArray(values)) {
    return [...array];
  }
  return array.filter((x) => !values.includes(x));
}

/**
 * Iterates over elements of collection, returning the first element predicate returns truthy for.
 * @param collection The collection to inspect.
 * @param predicate The function invoked per iteration.
 * @param fromIndex The index to search from.
 * @returns Returns the matched element, else undefined.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function find<T>(
  collection: T[],
  predicate: any,
  fromIndex = 0
): T | undefined {
  if (!Array.isArray(collection)) {
    return undefined;
  }

  const array = fromIndex > 0 ? collection.slice(fromIndex) : collection;

  if (typeof predicate === 'function') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return array.find(predicate as any);
  }

  if (typeof predicate === 'object' && predicate !== null) {
    return array.find((item) => {
      for (const key in predicate) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (get(item, key) !== (predicate as any)[key]) {
          return false;
        }
      }
      return true;
    });
  }

  // Default fallback
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return array.find((item) => (item as any) === predicate);
}
